import { useCurrentFrame } from 'remotion';
import { useTypingAnimation } from '../hooks/use-typing-animation';
import IdeShell from '../components/ide-shell';
import CodeEditorVideo from '../components/code-editor-video';
import OutputPanel from '../components/output-panel';
import IntelliSensePopup from '../components/intellisense-popup';

// Layout constants matching code-editor-video.jsx
const LINE_HEIGHT = 21;
const EDITOR_TOP = 30 + 28 + 35 + 22 + 12; // titlebar+toolbar+tabbar+breadcrumb+padding
const GUTTER_LEFT = 52 + 16 + 16;           // gutter+fold+pl-4
const CHAR_WIDTH = 8.4;

/**
 * Given a char index into a string, return { line (0-based), col (0-based) }.
 */
function charToLineCol(text, charIndex) {
  const slice = text.slice(0, charIndex);
  const lines = slice.split('\n');
  return { line: lines.length - 1, col: lines[lines.length - 1].length };
}

export default function CodeTypingScene({
  code = '',
  typingSpeed = 38,
  filename = 'Program.cs',
  projectName = 'HelloWorld',
  startFrom = 0,
  breakpoints,
  debugLine,
  intellisense = [],
}) {
  const frame = useCurrentFrame();
  const typing = useTypingAnimation(code, typingSpeed, startFrom);
  const lineCount = typing.text ? typing.text.split('\n').length : 1;

  // Find active intellisense trigger
  const activeIS = intellisense.find(
    (is) => typing.charIndex >= is.triggerAt && typing.charIndex < is.dismissAt
  );

  // Calculate popup position from trigger char position
  let popupX = 0;
  let popupY = 0;
  let appearFrame = 0;

  if (activeIS) {
    const { line, col } = charToLineCol(code, activeIS.triggerAt);
    // Scroll offset based on trigger position, not current lineCount (prevents drift)
    const triggerLineCount = code.slice(0, activeIS.triggerAt).split('\n').length;
    const scrollOffset = Math.max(0, (triggerLineCount - 22 + 3) * LINE_HEIGHT);
    popupX = GUTTER_LEFT + col * CHAR_WIDTH;
    popupY = EDITOR_TOP + (line + 1) * LINE_HEIGHT - scrollOffset;
    // Approximate frame when popup appeared (triggerAt chars typed)
    const framesPerChar = Math.max(1, Math.round((typingSpeed / 1000) * 60));
    appearFrame = (activeIS.triggerAt - startFrom) * framesPerChar;
  }

  const popup = activeIS ? (
    <IntelliSensePopup
      items={activeIS.items}
      selectedIndex={activeIS.selectedIndex ?? 0}
      visible={true}
      x={popupX}
      y={popupY}
      appearFrame={appearFrame}
    />
  ) : null;

  return (
    <IdeShell projectName={projectName} filename={filename} step={1} lineCount={lineCount} overlay={popup}>
      <CodeEditorVideo
        typedCode={typing.text}
        step={1}
        lineCount={lineCount}
        projectName={projectName}
        filename={filename}
        breakpoints={breakpoints}
        debugLine={debugLine}
      />
      <OutputPanel buildLogs={[]} />
    </IdeShell>
  );
}
